import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ShieldOff } from "lucide-react";
import { useGetConsentHistoryQuery } from "@/store/rest_api";
import { formatDateTime } from "@/util/date";

const CONSENT_TYPE_LABELS: Record<string, string> = {
  CALL: "Calling",
  SMS: "SMS",
  CALL_AND_SMS: "Calling & SMS",
  NONE: "None",
};

const SOURCE_LABELS: Record<string, string> = {
  WEB_FORM: "Web Form",
  VERBAL: "Verbal",
  IMPORT: "Import",
  MANUAL: "Manual",
  INTEGRATION: "Integration",
  SMS_KEYWORD: "Text reply",
  AI_CALL: "AI agent call",
  CALL_DISPOSITION: "Call wrap-up",
};

const BASIS_LABELS: Record<string, string> = {
  EXPRESS: "Express",
  WRITTEN: "Written",
};

interface ConsentHistoryProps {
  contactId: string;
}

const ConsentHistory: React.FC<ConsentHistoryProps> = ({ contactId }) => {
  const { data, isLoading } = useGetConsentHistoryQuery({
    contactId,
    page: 0,
    size: 10,
  });

  const events = data?.items ?? [];

  if (isLoading) return null;
  if (events.length === 0) return null;

  return (
    <div className="pt-2 border-t space-y-2">
      <p className="text-xs font-medium text-muted-foreground">History</p>
      {events.map((event) => (
        <div key={event.id} className="flex items-start gap-2 text-xs">
          {event.action === "GRANTED" ? (
            <ShieldCheck className="h-3.5 w-3.5 text-green-600 mt-0.5 shrink-0" />
          ) : (
            <ShieldOff className="h-3.5 w-3.5 text-destructive mt-0.5 shrink-0" />
          )}
          <div className="min-w-0">
            <span className="font-medium">{event.action === "GRANTED" ? "Granted" : "Revoked"}</span>{" "}
            <span className="text-muted-foreground">{CONSENT_TYPE_LABELS[event.consentType]}</span>
            {event.action === "GRANTED" && event.consentBasis && (
              <span className="text-muted-foreground"> ({BASIS_LABELS[event.consentBasis] ?? event.consentBasis})</span>
            )}
            {event.phoneNumberE164 && <span className="text-muted-foreground"> for {event.phoneNumberE164}</span>}
            <span className="text-muted-foreground">
              {" · "}
              {SOURCE_LABELS[event.source] ?? event.source}
              {" · "}
              {formatDateTime(event.createdAt)}
            </span>
            {event.membership?.user && (
              <span className="text-muted-foreground">
                {" · by "}
                {[event.membership.user.firstName, event.membership.user.lastName].filter(Boolean).join(" ")}
              </span>
            )}
            {event.notes && <p className="text-muted-foreground mt-0.5">{event.notes}</p>}
            {event.evidenceExcerpt && (
              <details className="mt-0.5">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  What was said
                </summary>
                <p className="mt-1 whitespace-pre-wrap rounded bg-muted px-2 py-1.5 text-muted-foreground">
                  {event.evidenceExcerpt}
                </p>
              </details>
            )}
            {event.evidenceCallId && (
              <Link
                to={`/calls/${event.evidenceCallId}`}
                className="mt-0.5 inline-block text-primary hover:underline"
              >
                View call
              </Link>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ConsentHistory;
